/**
 * 재추출 확인 창의 본문·푸터 버튼(spec `2026-09-29-처방검토-보험코드-형제전파-재추출-단계옵션`, D-14).
 *
 * 판단 로직은 순수 함수 `buildReextractConfirm(detail)`에 있고(reextractConfirm.ts), 여기서는
 * 그 모델을 화면으로 옮긴다. 버튼 3개([취소]·[Step 2부터 재추출]·[룰만 재추출])는 `ConfirmManager`의
 * `buttons` 배열로 넘겨 3버튼 그리드에 얹는다 — 라디오·폼 상태 없이 정적 버튼이다.
 */
import React from "react";
import styled from "styled-components";
import Button from "components/atoms/Button";
import { COLOR_SYSTEM } from "components/design/design-system";
import { ReextractConfirmModel } from "./reextractConfirm";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 360px;
  font-size: 14px;
  line-height: 1.5;
`;

/** 강등 경고 — 값이 아니라 주의 고지라 강조색(주황)으로 둔다. */
const Warning = styled.div`
  color: ${COLOR_SYSTEM.get("Orange")?.[700]};
`;

/** [룰만] 불가 사유 — 눌러도 소용없는 이유를 흐리게(회색) 붙인다. */
const Unavailable = styled.div`
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

export interface ReextractConfirmBodyProps {
  model: ReextractConfirmModel;
}

/** 확인 창 본문 — 본문 + 강등 경고 + (불가 시) 사유 한 줄. */
export const ReextractConfirmBody: React.FC<ReextractConfirmBodyProps> = ({ model }) => (
  <Body data-testid="reextract-confirm-body">
    <div>{model.bodyText}</div>
    <Warning>{model.warningText}</Warning>
    {model.unavailableText ? (
      <Unavailable data-testid="reextract-unavailable-reason">{model.unavailableText}</Unavailable>
    ) : null}
  </Body>
);

export interface ReextractConfirmActions {
  onCancel: () => void;
  /** [Step 2부터 재추출] — 현행 전체 재실행(`fromStep: "step2"`). */
  onStep2: () => void;
  /** [룰만 재추출] — 보관본으로 STEP3·4만(`fromStep: "step3"`). */
  onStep3: () => void;
}

/**
 * 푸터 버튼 3개. [Step 2부터]는 항상 활성, [룰만]은 모델의 `ruleOnlyEnabled`에 따른다(D-14).
 * `ConfirmManager.show({ buttons })`에 그대로 넣는다.
 */
export const buildReextractButtons = (
  model: ReextractConfirmModel,
  actions: ReextractConfirmActions
): React.ReactNode[] => [
  <Button
    key="cancel"
    buttonType="button"
    type="Text"
    size="Medium"
    variant="Ghost"
    color="Secondary"
    onClick={actions.onCancel}
  >
    취소
  </Button>,
  <Button
    key="step2"
    buttonType="button"
    type="Text"
    size="Medium"
    variant="Contained"
    color="Primary"
    onClick={actions.onStep2}
  >
    Step 2부터 재추출
  </Button>,
  <Button
    key="step3"
    buttonType="button"
    type="Text"
    size="Medium"
    variant="Outlined"
    color="Primary"
    disabled={!model.ruleOnlyEnabled}
    disabledColor
    onClick={actions.onStep3}
  >
    룰만 재추출
  </Button>,
];
